import React from 'react';

import { useCommunityViewContext } from 'components/View/CommunityViewContext';

import CMASummaryTableDataAddress from './CMASummaryTableDataAddress';
import CMASummaryTableDataCareTypes from './CMASummaryTableDataCareTypes';
import CMASummaryTableDataCommunityNotes from './CMASummaryTableDataCommunityNotes';
import CMASummaryTableDataContract from './CMASummaryTableDataContract';
import CMASummaryTableDataContractBenefit from './CMASummaryTableDataContractBenefit';
import CMASummaryTableDataContractType from './CMASummaryTableDataContractType';
import CMASummaryTableDataDistance from './CMASummaryTableDataDistance';
import CMASummaryTableDataFees from './CMASummaryTableDataFees';
import CMASummaryTableDataFeesGroup from './CMASummaryTableDataFeesGroup';
import CMASummaryTableDataGroupRoomComparison from './CMASummaryTableDataGroupRoomComparison';
import CMASummaryTableDataIncentives from './CMASummaryTableDataIncentives';
import CMASummaryTableDataLivingInfo from './CMASummaryTableDataLivingInfo';
import CMASummaryDataTableLivingInfoGroup from './CMASummaryTableDataLivingInfoGroup';
import CMASummaryTableDataLivingTypeFee from './CMASummaryTableDataLivingTypeFee';
import CMASummaryTableDataLivingTypeOccupancy from './CMASummaryTableDataLivingTypeOccupancy';
import CMASummaryTableDataOccupancy from './CMASummaryTableDataOccupancy';
import CMASummaryTableDataPhone from './CMASummaryTableDataPhone';
import CMASummaryTableDataReview from './CMASummaryTableDataReview';
import CMASummaryTableDataRoomComparison from './CMASummaryTableDataRoomComparison';
import CMASummaryTableDataShopDate from './CMASummaryTableDataShopDate';
import CMASummaryTableDataWebsite from './CMASummaryTableDataWebsite';
import CMASummaryTableDataYearOpened from './CMASummaryTableDataYearOpened';
import {
  CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW,
  CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW_ROOM_COMPARISON,
  CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW,
  CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW_ROOM_COMPARISON,
  CMA_SUMMARY_CARE_FEE_DATA_ROW,
  CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW,
  CMA_SUMMARY_COMMUNITY_CONTRACT_ROW,
  CMA_SUMMARY_CONTRACT_BENEFIT_DATA_ROW,
  CMA_SUMMARY_DATA_ROW_TYPE,
  CMA_SUMMARY_LIVING_TYPE_DATA_ROW,
  CMA_SUMMARY_LIVING_TYPE_FEE_DATA_ROW,
  CMA_SUMMARY_MEDICATION_FEE_DATA_ROW,
  CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW,
  CMA_SUMMARY_NOT_APPLICABLE_VALUE,
} from './constants';
import { getColumnInfo, getValueDataTestId } from './testIdUtils';
import { CMASummaryFilters } from './types';

type Props = {
  cmaSummaryRow: any;
  index: number;
  expandedRowKeys: string[];
};

const CMASummaryTableDataColumn = ({ cmaSummaryRow, index, expandedRowKeys }: Props) => {
  const { viewFilters } = useCommunityViewContext();
  const filters = viewFilters as CMASummaryFilters | undefined;
  let content;
  const rowMetadata = cmaSummaryRow[0];
  const dataItem = cmaSummaryRow[index];
  const hasCompetitorAverageColumn = filters?.viewOptions?.averageCompetitors !== false;

  const { competitorIndex } = getColumnInfo(index, hasCompetitorAverageColumn);
  const dataTestId = getValueDataTestId(rowMetadata, index, hasCompetitorAverageColumn);

  if (dataItem === CMA_SUMMARY_NOT_APPLICABLE_VALUE) {
    content = <span data-test-id={dataTestId}>{CMA_SUMMARY_NOT_APPLICABLE_VALUE}</span>;
  } else if (rowMetadata.type === CMA_SUMMARY_DATA_ROW_TYPE.ADDRESS) {
    content = <CMASummaryTableDataAddress address={dataItem} dataTestId={dataTestId} />;
  } else if (rowMetadata.type === CMA_SUMMARY_DATA_ROW_TYPE.DISTANCE) {
    content = <CMASummaryTableDataDistance distance={dataItem} dataTestId={dataTestId} />;
  } else if (rowMetadata.type === CMA_SUMMARY_DATA_ROW_TYPE.WEBSITE) {
    content = <CMASummaryTableDataWebsite website={dataItem} dataTestId={dataTestId} />;
  } else if (rowMetadata.type === CMA_SUMMARY_DATA_ROW_TYPE.PHONE) {
    content = <CMASummaryTableDataPhone phone={dataItem} dataTestId={dataTestId} />;
  } else if (rowMetadata.type === CMA_SUMMARY_DATA_ROW_TYPE.YEAR_OPENED) {
    content = <CMASummaryTableDataYearOpened yearOpened={dataItem} dataTestId={dataTestId} />;
  } else if (rowMetadata.type === CMA_SUMMARY_DATA_ROW_TYPE.NOTES) {
    content = <CMASummaryTableDataCommunityNotes notes={dataItem} dataTestId={dataTestId} />;
  } else if (rowMetadata.type === CMA_SUMMARY_DATA_ROW_TYPE.SHOP_DATE) {
    content = <CMASummaryTableDataShopDate shopDate={dataItem} dataTestId={dataTestId} />;
  } else if (rowMetadata.type === CMA_SUMMARY_DATA_ROW_TYPE.REVIEWS) {
    content = (
      <CMASummaryTableDataReview reviews={dataItem} expandedRowKeys={expandedRowKeys} dataTestId={dataTestId} />
    );
  } else if (rowMetadata.type === CMA_SUMMARY_DATA_ROW_TYPE.CARE_TYPES) {
    content = <CMASummaryTableDataCareTypes careTypes={dataItem} dataTestId={dataTestId} />;
  } else if (rowMetadata.type === CMA_SUMMARY_DATA_ROW_TYPE.CONTRACT_TYPE) {
    content = <CMASummaryTableDataContractType contractType={dataItem} dataTestId={dataTestId} />;
  } else if (rowMetadata.type === CMA_SUMMARY_DATA_ROW_TYPE.OCCUPANCY) {
    content = <CMASummaryTableDataOccupancy occupancies={dataItem} dataTestId={dataTestId} />;
  } else if (rowMetadata.type === CMA_SUMMARY_DATA_ROW_TYPE.INCENTIVES) {
    content = (
      <CMASummaryTableDataIncentives incentives={dataItem} dataTestId={dataTestId} competitorIndex={competitorIndex} />
    );
  } else if (rowMetadata.type === CMA_SUMMARY_LIVING_TYPE_DATA_ROW) {
    content = <CMASummaryTableDataLivingTypeOccupancy occupancy={dataItem} dataTestId={dataTestId} />;
  } else if (rowMetadata.type === CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW) {
    content = <CMASummaryTableDataLivingInfo livingInfo={dataItem} dataTestId={dataTestId} />;
  } else if (rowMetadata.type === CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW) {
    content = <CMASummaryDataTableLivingInfoGroup livingInfo={dataItem} dataTestId={dataTestId} />;
  } else if (rowMetadata.type === CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW) {
    content = (
      <CMASummaryTableDataFeesGroup
        fees={dataItem}
        aggregationType={filters?.careLevelsStatsType}
        dataTestId={dataTestId}
      />
    );
  } else if (rowMetadata.type === CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW) {
    content = (
      <CMASummaryTableDataFeesGroup
        fees={dataItem}
        aggregationType={filters?.medicationLevelsStatsType}
        dataTestId={dataTestId}
      />
    );
  } else if (
    rowMetadata.type === CMA_SUMMARY_CARE_FEE_DATA_ROW ||
    rowMetadata.type === CMA_SUMMARY_MEDICATION_FEE_DATA_ROW
  ) {
    content = <CMASummaryTableDataFees feeLevel={dataItem} dataTestId={dataTestId} />;
  } else if (rowMetadata.type === CMA_SUMMARY_LIVING_TYPE_FEE_DATA_ROW) {
    content = <CMASummaryTableDataLivingTypeFee fee={dataItem} dataTestId={dataTestId} />;
  } else if (rowMetadata.type === CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW_ROOM_COMPARISON) {
    content = <CMASummaryTableDataRoomComparison roomData={dataItem} dataTestId={dataTestId} />;
  } else if (rowMetadata.type === CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW_ROOM_COMPARISON) {
    content = <CMASummaryTableDataGroupRoomComparison apartmentTypeGroupData={dataItem} dataTestId={dataTestId} />;
  } else if (rowMetadata.type === CMA_SUMMARY_CONTRACT_BENEFIT_DATA_ROW) {
    content = <CMASummaryTableDataContractBenefit benefits={dataItem} dataTestId={dataTestId} />;
  } else if (rowMetadata.type === CMA_SUMMARY_COMMUNITY_CONTRACT_ROW) {
    content = (
      <CMASummaryTableDataContract
        contract={dataItem}
        rowKey={rowMetadata.key}
        expandedRowKeys={expandedRowKeys}
        dataTestId={dataTestId}
      />
    );
  }

  return <>{content}</>;
};

export default CMASummaryTableDataColumn;
